import { motion } from "@/lib/motion-lite";
import { Brush, Languages, DollarSign, Users, Layers, MapPinned } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

const reasons = [
  {
    icon: Brush,
    title: "Prep Is the Job",
    desc: "Patching, sanding, caulking, and priming happen before a finish coat ever goes on. That's the difference between paint that lasts a decade and paint that peels in two Colorado winters.",
    accent: "secondary" as const,
  },
  {
    icon: Layers,
    title: "One Crew, Every Trade",
    desc: "Painting, drywall, tile, waterproofing, and remodeling under one roof. No chasing five contractors and no trade undoing another's work.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Free, Written, Itemized",
    desc: "Every estimate is free, in writing, and broken out line by line — so you see exactly what you're paying for before anything starts.",
    accent: "secondary" as const,
  },
  {
    icon: Languages,
    title: "Bilingual — English & Español",
    desc: "Our whole team works in both languages. Hablamos español, y con gusto le explicamos cada detalle de su proyecto en su idioma.",
    accent: "primary" as const,
  },
  {
    icon: Users,
    title: "Family-Run, Owner on Site",
    desc: `${siteConfig.owner} and his brother run this company and work the jobs. You're not handed off to a rotating subcontractor crew after you sign.`,
    accent: "secondary" as const,
  },
  {
    icon: MapPinned,
    title: "Built for Colorado",
    desc: "High-altitude UV, hail, and freeze-thaw destroy the wrong coatings. We spec products rated for this climate — from Highlands Ranch to the foothills to Loveland.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden cv-section">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why Diaz Brothers
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-accent">Us</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons Colorado homeowners and business owners keep our number saved.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div
                className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                  reason.accent === "secondary"
                    ? "bg-secondary/25 text-secondary"
                    : "bg-white/15 text-white"
                }`}
              >
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">
                {reason.title}
              </h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
